import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, ChevronDown, ChevronUp } from 'lucide-react';
import SEO from '../components/SEO';
import { faqPageSchema, serviceSchema } from '../utils/structuredData';
import CrisisSupport from '../components/CrisisSupport';
import ScrollIndicator from '../components/ScrollIndicator';
import './AssessmentPage.css';

/**
 * Shared assessment page template.
 * Props:
 *   seo          – { title, description, canonicalUrl }
 *   heroTitle    – string
 *   heroSubtitle – string
 *   intro        – JSX or string (placeholder if not yet supplied)
 *   whoFor       – JSX or string
 *   whatsInvolved – JSX or string
 *   reportUsedFor – JSX or string
 *   feesContent  – JSX (optional, overrides default fees section)
 *   faqs         – [{ question, answer }]  (also emitted as FAQPage schema; answers are
 *                  always in the HTML, hidden until opened, so crawlers can read them)
 *   serviceName  – name for the Service structured data (defaults to heroTitle)
 *   ctaText      – string (button label, defaults to "Make an Enquiry")
 */
const AssessmentPage = ({
  seo,
  heroKicker = 'Assessment',
  heroTitle,
  heroSubtitle,
  intro,
  afterIntro,
  whoFor,
  whatsInvolved,
  extraSections,
  reportUsedFor,
  feesContent,
  faqs = [],
  serviceName,
  ctaText = 'Make an Enquiry',
  enquiryParam = ''
}) => {
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) entry.target.classList.add('revealed');
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });
    const els = document.querySelectorAll('.scroll-reveal');
    els.forEach((el) => observer.observe(el));
    return () => els.forEach((el) => observer.unobserve(el));
  }, []);

  // Structured data: the page's FAQs exactly as shown, and the service itself
  const schemas = [
    faqs.length > 0 ? faqPageSchema(faqs) : null,
    serviceSchema({ name: serviceName || heroTitle, description: seo.description, url: seo.canonicalUrl }),
  ].filter(Boolean);

  return (
    <div className="page-assessment" data-testid="assessment-page">
      <SEO
        title={seo.title}
        description={seo.description}
        canonicalUrl={seo.canonicalUrl}
        schemaData={schemas}
      />
      <ScrollIndicator />

      {/* Hero */}
      <section id="main-content" className="interior-hero" data-testid="page-hero">
        <div className="container">
          <span className="section-label">{heroKicker}</span>
          <h1 className="heading-hero">{heroTitle}</h1>
          {heroSubtitle && <p className="hero-subline">{heroSubtitle}</p>}
        </div>
      </section>

      {/* Intro */}
      <section className="assess-section scroll-reveal" data-testid="assess-intro">
        <div className="container assess-narrow">
          <div className="assess-intro-content">{intro}</div>
        </div>
      </section>

      {/* After-intro sections (page-specific) */}
      {afterIntro}

      {/* Who this assessment is for */}
      <section className="assess-section assess-alt scroll-reveal" data-testid="assess-who">
        <div className="container assess-narrow">
          <span className="section-label">Who is it for</span>
          <h2 className="heading-1">Who this assessment is for</h2>
          <div className="assess-body">{whoFor}</div>
        </div>
      </section>

      {/* What's involved */}
      <section className="assess-section scroll-reveal" data-testid="assess-involved">
        <div className="container assess-narrow">
          <span className="section-label">The Process</span>
          <h2 className="heading-1">What's involved</h2>
          <div className="assess-body">{whatsInvolved}</div>
        </div>
      </section>

      {/* Personal, start to finish card */}
      <section className="assess-section assess-alt scroll-reveal" data-testid="assess-personal-card">
        <div className="container assess-narrow">
          <div className="assess-personal-box">
            <h3 className="heading-3">Personal, start to finish</h3>
            <ul>
              <li>An hour with parents or caregivers before we begin</li>
              <li>At least two assessment sessions, on different days</li>
              <li>An hour of plain-language feedback</li>
              <li>Recommendations for home, school or work</li>
              <li>A personal profile written for the person assessed</li>
            </ul>
            <p className="assess-personal-closing"><strong>Every part is done by me. Nothing is outsourced.</strong></p>
          </div>
        </div>
      </section>

      {/* Extra sections (page-specific) */}
      {extraSections}

      {/* What the report can be used for */}
      <section className="assess-section assess-alt scroll-reveal" data-testid="assess-report">
        <div className="container assess-narrow">
          <span className="section-label">Your Report</span>
          <h2 className="heading-1">What the report can be used for</h2>
          <div className="assess-body">{reportUsedFor}</div>
        </div>
      </section>

      {/* Fees and rebates */}
      <section className="assess-section scroll-reveal" data-testid="assess-fees">
        <div className="container assess-narrow">
          <span className="section-label">Investment</span>
          <h2 className="heading-1">Fees and rebates</h2>
          <div className="assess-body">
            {feesContent || (
              <>
                <p className="body-medium">
                  Assessment fees vary depending on the type and scope of assessment required.
                  Medicare rebates may be available with a Mental Health Treatment Plan from your GP,
                  and NDIS funding can cover assessments and reports.
                </p>
                <p className="body-medium" style={{ marginTop: '1rem' }}>
                  <Link to="/fees" className="assess-link">View our full fee information →</Link>
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Enquiry CTA */}
      <section className="assess-cta-section" data-testid="assess-cta">
        <div className="container">
          <div className="assess-cta-card">
            <h2 className="heading-2">Ready to find out more?</h2>
            <p className="body-large">Send us an enquiry or give us a call. We're happy to answer your questions.</p>
            <div className="assess-cta-buttons">
              <Link
                to={enquiryParam ? `/contact?enquiry=${enquiryParam}` : '/contact'}
                className="btn-primary"
                data-testid="assess-enquiry-btn"
              >
                {ctaText}
              </Link>
              <a href="tel:0266002710" className="assess-phone-cta" data-testid="assess-phone-cta">
                <Phone size={18} /> 02 6600 2710
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="assess-section scroll-reveal" data-testid="assess-faq">
          <div className="container assess-narrow">
            <span className="section-label">FAQ</span>
            <h2 className="heading-1">Frequently asked questions</h2>
            <div className="assess-faq-list">
              {faqs.map((faq, i) => (
                <div key={i} className={`assess-faq-item ${openFaq === i ? 'open' : ''}`}>
                  <button
                    className="assess-faq-question"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    data-testid={`faq-q-${i}`}
                  >
                    <span>{faq.question}</span>
                    {openFaq === i ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                  <div className="assess-faq-answer" data-testid={`faq-a-${i}`} hidden={openFaq !== i}>
                    <p className="body-medium">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <CrisisSupport />
    </div>
  );
};

export default AssessmentPage;
